import React, { FormEvent, ReactNode, useMemo, useRef, useState } from "react";
import {
  ClientMutationFields,
  PortalPatient,
  PortalRecordFormField,
  PortalRelationOption,
} from "../api/contracts";
import { ClinicianPortalApiError } from "../api/clinicianPortalApi";
import { sortLovOptions } from "../config/lov";
import {
  getConfiguredPillToken,
  getConfiguredValuePillToken,
} from "../config/palette";
import { MultiSelectPillField } from "./MultiSelectPillField";
import { PortalLink } from "./PortalLink";
import { SingleSelectPillField } from "./SingleSelectPillField";
import {
  ContentSection,
  RecordDetailCard,
  RecordFieldRow,
} from "./DataDisplay";

type ClientFieldKey =
  | "accessCode"
  | "clinicId"
  | "nickname"
  | "status"
  | "clinicalSummary"
  | "highSudsThreshold"
  | "sudsInputStyle"
  | "selfInitiatedPermitted"
  | "copingStrategies"
  | "funActivities"
  | "pointsEarned"
  | "coins"
  | "badgeLevel"
  | "badgeCollection";

interface ClientFormValues {
  accessCode: string;
  clinicId: string;
  nickname: string;
  status: string;
  clinicalSummary: string;
  highSudsThreshold: string;
  sudsInputStyle: string;
  selfInitiatedPermitted: boolean;
  copingStrategies: string[];
  funActivities: string[];
  pointsEarned: string;
  coins: string;
  badgeLevel: string;
  badgeCollection: string[];
}

type ClientFormErrors = Partial<Record<ClientFieldKey | "form", string>>;

const editableFieldKeys: readonly (keyof ClientMutationFields)[] = [
  "clinicId",
  "nickname",
  "status",
  "clinicalSummary",
  "highSudsThreshold",
  "sudsInputStyle",
  "selfInitiatedPermitted",
  "copingStrategies",
  "funActivities",
  "pointsEarned",
  "coins",
  "badgeCollection",
];

const clientValidationCodes = new Set([
  "INVALID_CLIENT_CREATE_REQUEST",
  "INVALID_CLIENT_UPDATE_REQUEST",
  "REQUIRED_FIELD_MISSING",
  "INVALID_LOV_VALUE",
  "DISABLED_LOV_VALUE",
  "INVALID_FIELD_TYPE",
  "INVALID_INTEGER",
  "VALUE_BELOW_MINIMUM",
  "VALUE_ABOVE_MAXIMUM",
  "INVALID_HIGH_SUDS_THRESHOLD",
  "INVALID_POINTS",
  "INVALID_COINS",
  "NO_CLIENT_FIELDS_PROVIDED",
  "FIELD_NOT_EDITABLE",
  "CLINIC_NOT_AVAILABLE",
  "INVALID_NICKNAME",
  "INVALID_STATUS",
  "INVALID_SUDS_INPUT_STYLE",
  "INVALID_COPING_STRATEGIES",
  "INVALID_FUN_ACTIVITIES",
  "INVALID_PATIENT_UPDATE",
  "CLINIC_REQUIRED",
]);

function submissionErrorMessage(error: unknown): string | undefined {
  if (!error) return undefined;
  if (!(error instanceof ClinicianPortalApiError)) {
    return "Client could not be saved. Try again.";
  }
  if (error.code === "NOT_AUTHORIZED" || error.code === "NOT_FOUND") {
    return "This Client is unavailable.";
  }
  if (
    error.code === "INVALID_REQUEST" ||
    clientValidationCodes.has(error.code)
  ) {
    return "Review the Client form values and try again.";
  }
  return "Client could not be saved. Try again.";
}

function metadataMap(fields: readonly PortalRecordFormField[]) {
  return new Map(
    fields
      .filter((field) => field.enabled)
      .map((field) => [field.fieldKey as ClientFieldKey, field] as const),
  );
}

function scalarDefault(field: PortalRecordFormField | undefined): unknown {
  return field?.defaultValue ?? undefined;
}

function lovDefaults(field: PortalRecordFormField | undefined): string[] {
  if (!field?.catalog) return [];
  return sortLovOptions(field.catalog.options, field.catalog.orderingMode)
    .filter((option) => option.enabled && option.isDefault)
    .map((option) => option.value);
}

function lovDefaultNames(field: PortalRecordFormField | undefined): string[] {
  if (!field?.catalog) return [];
  return sortLovOptions(field.catalog.options, field.catalog.orderingMode)
    .filter((option) => option.enabled && option.isDefault)
    .map((option) => option.name);
}

function formatDefaultList(items: readonly string[]): string {
  if (items.length <= 1) return items[0] ?? "";
  if (items.length === 2) return `${items[0]} and ${items[1]}`;
  return `${items.slice(0, -1).join(", ")}, and ${items[items.length - 1]}`;
}

function createDefaultsCallout(
  fields: readonly PortalRecordFormField[],
  values: ClientFormValues,
): string {
  const metadata = metadataMap(fields);
  const defaults = [
    ...lovDefaultNames(metadata.get("status")).map((name) => `${name} status`),
    values.highSudsThreshold
      ? `High SUDS threshold ${values.highSudsThreshold}`
      : "",
    ...lovDefaultNames(metadata.get("sudsInputStyle")),
    ...lovDefaultNames(metadata.get("copingStrategies")),
    `${values.coins} coins`,
    ...lovDefaultNames(metadata.get("badgeCollection")).map(
      (name) => `${name} badge`,
    ),
  ].filter(Boolean);

  return `${formatDefaultList(defaults)}. You can change any of these before saving.`;
}

export function createClientFormValues(
  fields: readonly PortalRecordFormField[],
  relationOptions: readonly PortalRelationOption[] = [],
): ClientFormValues {
  const metadata = metadataMap(fields);
  const stringDefault = (key: ClientFieldKey) => {
    const value = scalarDefault(metadata.get(key));
    return typeof value === "string" ? value : "";
  };
  const numberDefault = (key: ClientFieldKey) => {
    const value = scalarDefault(metadata.get(key));
    if (typeof value === "number" && Number.isInteger(value)) {
      return String(value);
    }
    if (
      typeof value === "string" &&
      value.trim() !== "" &&
      Number.isInteger(Number(value))
    ) {
      return String(Number(value));
    }
    return "";
  };
  const booleanDefault = (key: ClientFieldKey) => {
    const value = scalarDefault(metadata.get(key));
    return typeof value === "boolean" ? value : false;
  };
  const enabledClinics = relationOptions.filter((option) => option.enabled);
  const clinicId =
    enabledClinics.length === 1 ? enabledClinics[0].value : "";

  return {
    accessCode: "",
    clinicId,
    nickname: stringDefault("nickname"),
    status: lovDefaults(metadata.get("status"))[0] ?? "",
    clinicalSummary: stringDefault("clinicalSummary"),
    highSudsThreshold: numberDefault("highSudsThreshold"),
    sudsInputStyle: lovDefaults(metadata.get("sudsInputStyle"))[0] ?? "",
    selfInitiatedPermitted: booleanDefault("selfInitiatedPermitted"),
    copingStrategies: lovDefaults(metadata.get("copingStrategies")),
    funActivities: lovDefaults(metadata.get("funActivities")),
    pointsEarned: numberDefault("pointsEarned"),
    // Coins has an explicit Add Client product default. Do not let the
    // currently configured legacy value of 0 override it.
    coins: "20",
    badgeLevel: "",
    badgeCollection: lovDefaults(metadata.get("badgeCollection")),
  };
}

function createDefaultSubmissionKeys(
  fields: readonly PortalRecordFormField[],
): Set<ClientFieldKey> {
  const metadata = metadataMap(fields);
  const keys = new Set<ClientFieldKey>();
  const includeWhenEditableOnCreate = (key: ClientFieldKey) => {
    const config = metadata.get(key);
    if (config?.visibleOnCreate && config.editableOnCreate) keys.add(key);
  };

  if (lovDefaults(metadata.get("copingStrategies")).length > 0) {
    includeWhenEditableOnCreate("copingStrategies");
  }
  if (lovDefaults(metadata.get("badgeCollection")).length > 0) {
    includeWhenEditableOnCreate("badgeCollection");
  }
  includeWhenEditableOnCreate("coins");

  return keys;
}

function editClientFormValues(patient: PortalPatient): ClientFormValues {
  return {
    accessCode: patient.accessCode,
    clinicId: patient.clinicId,
    nickname: patient.nickname,
    status: patient.status,
    clinicalSummary: patient.clinicalSummary,
    highSudsThreshold: String(patient.highSudsThreshold),
    sudsInputStyle: patient.sudsInputStyle,
    selfInitiatedPermitted: patient.selfInitiatedPermitted,
    copingStrategies: [...patient.copingStrategies],
    funActivities: [...patient.funActivities],
    pointsEarned: String(patient.pointsEarned),
    coins: String(patient.coins),
    badgeLevel: patient.badgeLevel,
    badgeCollection: [...patient.badgeCollection],
  };
}

function arraysEqual(left: readonly string[], right: readonly string[]) {
  return (
    left.length === right.length &&
    left.every((value, index) => value === right[index])
  );
}

export function buildSparseClientUpdate(
  values: ClientMutationFields,
  initial: ClientMutationFields,
): ClientMutationFields {
  const update: ClientMutationFields = {};
  editableFieldKeys.forEach((key) => {
    const value = values[key];
    const initialValue = initial[key];
    const equal =
      Array.isArray(value) && Array.isArray(initialValue)
        ? arraysEqual(value, initialValue)
        : value === initialValue;
    if (!equal) {
      Object.assign(update, { [key]: value });
    }
  });
  return update;
}

function mutationValues(values: ClientFormValues): ClientMutationFields {
  return {
    clinicId: values.clinicId,
    nickname: values.nickname.trim(),
    status: values.status,
    clinicalSummary: values.clinicalSummary,
    highSudsThreshold: Number(values.highSudsThreshold),
    sudsInputStyle: values.sudsInputStyle,
    selfInitiatedPermitted: values.selfInitiatedPermitted,
    copingStrategies: [...values.copingStrategies],
    funActivities: [...values.funActivities],
    pointsEarned: Number(values.pointsEarned),
    coins: Number(values.coins),
    badgeCollection: [...values.badgeCollection],
  };
}

function initialMutationValues(patient: PortalPatient): ClientMutationFields {
  return {
    clinicId: patient.clinicId,
    nickname: patient.nickname.trim(),
    status: patient.status,
    clinicalSummary: patient.clinicalSummary,
    highSudsThreshold: patient.highSudsThreshold,
    sudsInputStyle: patient.sudsInputStyle,
    selfInitiatedPermitted: patient.selfInitiatedPermitted,
    copingStrategies: [...patient.copingStrategies],
    funActivities: [...patient.funActivities],
    pointsEarned: patient.pointsEarned,
    coins: patient.coins,
    badgeCollection: [...patient.badgeCollection],
  };
}

const clientFieldHelp: Partial<Record<ClientFieldKey, ReactNode>> = {
  clinicalSummary:
    "Brief summary of OCD themes, common triggers, obsessions/compulsions, avoidance and reassurance patterns, plus any care-relevant notes (e.g., homework adherence or frequent step modifications). Avoid personal identifiers.",
  highSudsThreshold: (
    <>
      If the client reports a SUDS level <strong>at or above (≥) this number</strong>,
      the assistant encourages coping skills. <strong>Default: 7.</strong>
      <br />
      Set to <strong>100</strong> to disable the assistant prompting for coping
      skills.
    </>
  ),
  selfInitiatedPermitted:
    "When checked, assistant will allow client to report Self-Initiated Exposure.",
  sudsInputStyle: (
    <>
      Choose how the client enters SUDS ratings when prompted, such as{" "}
      <strong>Number</strong>, <strong>Slider</strong>, or <strong>Dial</strong>.
      The default is Number.
    </>
  ),
  copingStrategies:
    "List the client's favorite or preferred coping strategies.",
  funActivities:
    "List the client's favorite activities (behavioral activation).",
  pointsEarned:
    "Points the client earned through homework completion. Points are used to advance the client’s rank.",
  coins:
    "Coins client earned by client in completing homework. Client can use earn coins to unlock wardrobe to dress your Capybara.",
};

function FieldHelp({
  field,
}: {
  field: PortalRecordFormField;
}) {
  const help =
    clientFieldHelp[field.fieldKey as ClientFieldKey] ?? field.helpText;
  return help ? <small className="cp-client-form-help">{help}</small> : null;
}

function FormField({
  field,
  children,
}: {
  field: PortalRecordFormField;
  children: ReactNode;
}) {
  return (
    <div className="cp-form-field">
      <FieldHelp field={field} />
      {children}
    </div>
  );
}

export function ClientForm({
  mode,
  fields,
  relationOptions,
  patient,
  clinicianCode,
  cancelPath,
  isSubmitting,
  submissionError,
  clearSubmissionError,
  onSubmit,
}: {
  mode: "create" | "edit";
  fields: readonly PortalRecordFormField[];
  relationOptions: readonly PortalRelationOption[];
  patient?: PortalPatient;
  clinicianCode: string;
  cancelPath: string;
  isSubmitting: boolean;
  submissionError?: unknown;
  clearSubmissionError: () => void;
  onSubmit: (values: ClientMutationFields) => Promise<void>;
}) {
  const metadata = useMemo(() => metadataMap(fields), [fields]);
  const [initialValues] = useState(() =>
    mode === "edit" && patient
      ? editClientFormValues(patient)
      : createClientFormValues(fields, relationOptions),
  );
  const [values, setValues] = useState(initialValues);
  const [defaultSubmissionKeys] = useState(() =>
    mode === "create" ? createDefaultSubmissionKeys(fields) : new Set(),
  );
  const [defaultsCallout] = useState(() =>
    mode === "create" ? createDefaultsCallout(fields, initialValues) : "",
  );
  const [dirtyFields, setDirtyFields] = useState<Set<ClientFieldKey>>(
    new Set(),
  );
  const [validationErrors, setValidationErrors] = useState<ClientFormErrors>(
    {},
  );
  const submittingRef = useRef(false);
  const apiError = submissionErrorMessage(submissionError);

  const field = (key: ClientFieldKey) => {
    const config = metadata.get(key);
    if (!config) return undefined;
    const visible =
      mode === "create" ? config.visibleOnCreate : config.visibleOnEdit;
    return visible ? config : undefined;
  };
  const editable = (config: PortalRecordFormField) =>
    mode === "create" ? config.editableOnCreate : config.editableOnEdit;
  const required = (config: PortalRecordFormField) =>
    config.required || config.fieldKey === "clinicId";

  const change = <K extends ClientFieldKey>(
    key: K,
    value: ClientFormValues[K],
  ) => {
    setValues((current) => ({ ...current, [key]: value }));
    setDirtyFields((current) => new Set(current).add(key));
    setValidationErrors((current) => {
      if (!current[key] && !current.form) return current;
      const next = { ...current };
      delete next[key];
      delete next.form;
      return next;
    });
    if (!isSubmitting) clearSubmissionError();
  };

  const validateInteger = (
    key: "highSudsThreshold" | "pointsEarned" | "coins",
    config: PortalRecordFormField,
    nextErrors: ClientFormErrors,
  ) => {
    const input = values[key].trim();
    if (!input) {
      if (config.required || dirtyFields.has(key)) {
        nextErrors[key] = `${config.label} must be an integer.`;
      }
      return;
    }
    const number = Number(input);
    if (!Number.isInteger(number)) {
      nextErrors[key] = `${config.label} must be an integer.`;
      return;
    }
    if (key === "highSudsThreshold") {
      if (!((number >= 1 && number <= 10) || number === 100)) {
        nextErrors[key] =
          `${config.label} must be an integer from 1 to 10, or 100.`;
      }
      return;
    }
    if (config.minValue !== null && number < config.minValue) {
      nextErrors[key] = `${config.label} must be at least ${config.minValue}.`;
    } else if (config.maxValue !== null && number > config.maxValue) {
      nextErrors[key] = `${config.label} must be at most ${config.maxValue}.`;
    }
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (isSubmitting || submittingRef.current) return;

    const nextErrors: ClientFormErrors = {};
    const nicknameField = field("nickname");
    if (!values.nickname.trim()) {
      nextErrors.nickname = `${nicknameField?.label ?? "Nickname"} is required.`;
    }
    const clinicalSummaryField = field("clinicalSummary");
    if (clinicalSummaryField?.required && !values.clinicalSummary.trim()) {
      nextErrors.clinicalSummary = `${clinicalSummaryField.label} is required.`;
    }
    const requiredSingleValueKeys: readonly (
      | "clinicId"
      | "status"
      | "sudsInputStyle"
    )[] = ["clinicId", "status", "sudsInputStyle"];
    requiredSingleValueKeys.forEach((key) => {
      const config = field(key);
      if (config && required(config) && !values[key]) {
        nextErrors[key] = `${config.label} is required.`;
      }
    });
    (["copingStrategies", "funActivities", "badgeCollection"] as const).forEach(
      (key) => {
        const config = field(key);
        if (config?.required && !values[key].length) {
          nextErrors[key] = `${config.label} is required.`;
        }
      },
    );
    (["highSudsThreshold", "pointsEarned", "coins"] as const).forEach((key) => {
      const config = field(key);
      if (config) validateInteger(key, config, nextErrors);
    });

    setValidationErrors(nextErrors);
    clearSubmissionError();
    if (Object.keys(nextErrors).length) return;

    const allValues = mutationValues(values);
    let submission: ClientMutationFields;
    if (mode === "edit" && patient) {
      submission = buildSparseClientUpdate(
        allValues,
        initialMutationValues(patient),
      );
      if (!Object.keys(submission).length) {
        setValidationErrors({
          form: "Make at least one change before saving.",
        });
        return;
      }
    } else {
      submission = {
        nickname: allValues.nickname,
        clinicId: allValues.clinicId,
      };
      editableFieldKeys.forEach((key) => {
        if (
          key !== "nickname" &&
          key !== "clinicId" &&
          (dirtyFields.has(key as ClientFieldKey) ||
            defaultSubmissionKeys.has(key as ClientFieldKey))
        ) {
          Object.assign(submission, { [key]: allValues[key] });
        }
      });
    }

    submittingRef.current = true;
    try {
      await onSubmit(submission);
    } finally {
      submittingRef.current = false;
    }
  };

  const label = (config: PortalRecordFormField, htmlFor?: string) => (
    <label className="cp-visually-hidden" htmlFor={htmlFor}>
      {config.label}{" "}
      {required(config) ? <span aria-hidden="true">*</span> : null}
    </label>
  );

  const error = (key: ClientFieldKey) =>
    validationErrors[key] ? (
      <p className="cp-form-error" id={`${mode}-client-${key}-error`}>
        {validationErrors[key]}
      </p>
    ) : null;

  const renderReadonly = (
    key: "accessCode" | "badgeLevel",
    placeholder: string,
  ) => {
    const config = field(key);
    if (!config) return null;
    const id = `${mode}-client-${key}`;
    return (
      <FormField field={config}>
        {label(config, id)}
        <input
          id={id}
          className="cp-form-input"
          type="text"
          value={values[key]}
          placeholder={placeholder}
          readOnly
        />
      </FormField>
    );
  };

  const renderSingleLov = (key: "status" | "sudsInputStyle") => {
    const config = field(key);
    if (!config?.catalog) return null;
    const id = `${mode}-client-${key}`;
    const options = sortLovOptions(
      config.catalog.options,
      config.catalog.orderingMode,
    ).filter(
      (option) =>
        option.enabled || (mode === "edit" && option.value === values[key]),
    );
    const presentation = {
      status: {
        ariaLabel: "Choose Client Status",
        searchPlaceholder: "Find a Client Status",
        emptyMessage: "No Client Statuses found",
      },
      sudsInputStyle: {
        ariaLabel: "Choose SUDS Entry Style",
        searchPlaceholder: "Find a SUDS Entry Style",
        emptyMessage: "No SUDS Entry Styles found",
      },
    }[key];
    return (
      <FormField field={config}>
        {label(config, id)}
        <SingleSelectPillField
          id={id}
          value={values[key]}
          options={options.map((option) => ({
            id: option.id,
            value: option.value,
            label: option.name,
            colorKey: option.colorKey,
            disabled: !option.enabled,
          }))}
          onChange={(nextValue) => change(key, nextValue)}
          ariaLabel={presentation.ariaLabel}
          ariaDescribedBy={
            validationErrors[key] ? `${mode}-client-${key}-error` : undefined
          }
          placeholder={`Select ${config.label}`}
          searchPlaceholder={presentation.searchPlaceholder}
          emptyMessage={presentation.emptyMessage}
          resolveColor={(option) => getConfiguredPillToken(option.colorKey)}
          disabled={!editable(config) || isSubmitting}
          invalid={Boolean(validationErrors[key])}
          clearable={!config.required}
        />
        {error(key)}
      </FormField>
    );
  };

  const renderMultiLov = (
    key: "copingStrategies" | "funActivities" | "badgeCollection",
  ) => {
    const config = field(key);
    if (!config?.catalog) return null;
    const options = sortLovOptions(
      config.catalog.options,
      config.catalog.orderingMode,
    ).filter(
      (option) =>
        option.enabled ||
        (mode === "edit" && values[key].includes(option.value)),
    );
    const presentation = {
      copingStrategies: {
        kind: "coping" as const,
        ariaLabel: "Choose coping strategies",
        placeholder: "Select coping strategies",
        searchPlaceholder: "Find a coping strategy",
        emptyMessage: "No coping strategies found",
      },
      funActivities: {
        kind: "fun" as const,
        ariaLabel: "Choose fun activities",
        placeholder: "Select fun activities",
        searchPlaceholder: "Find a fun activity",
        emptyMessage: "No fun activities found",
      },
      badgeCollection: {
        kind: "badge" as const,
        ariaLabel: "Choose badges",
        placeholder: "Select badges",
        searchPlaceholder: "Find a badge",
        emptyMessage: "No badges found",
      },
    }[key];
    return (
      <fieldset className="cp-form-field cp-form-fieldset">
        <legend className="cp-visually-hidden">
          {config.label}{" "}
          {config.required ? <span aria-hidden="true">*</span> : null}
        </legend>
        <FieldHelp field={config} />
        <MultiSelectPillField
          value={values[key]}
          options={options.map((option) => ({
            id: option.id,
            value: option.value,
            label: option.name,
            colorKey: option.colorKey,
            disabled: !option.enabled,
          }))}
          onChange={(nextValue) => change(key, nextValue)}
          ariaLabel={presentation.ariaLabel}
          placeholder={presentation.placeholder}
          searchPlaceholder={presentation.searchPlaceholder}
          emptyMessage={presentation.emptyMessage}
          resolveColor={(option) =>
            getConfiguredValuePillToken(
              option.value,
              presentation.kind,
              option.colorKey,
              undefined,
              option.label,
            )
          }
          disabled={!editable(config) || isSubmitting}
          invalid={Boolean(validationErrors[key])}
        />
        {error(key)}
      </fieldset>
    );
  };

  const renderInteger = (
    key: "highSudsThreshold" | "pointsEarned" | "coins",
  ) => {
    const config = field(key);
    if (!config) return null;
    const id = `${mode}-client-${key}`;
    return (
      <FormField field={config}>
        {label(config, id)}
        <input
          id={id}
          className={`cp-form-input${
            key === "highSudsThreshold" ? "" : " cp-form-input--no-spinner"
          }`}
          type="number"
          step="1"
          value={values[key]}
          required={config.required}
          readOnly={!editable(config)}
          disabled={isSubmitting}
          aria-invalid={Boolean(validationErrors[key])}
          onChange={(event) => change(key, event.target.value)}
        />
        {error(key)}
      </FormField>
    );
  };

  const accessCode = renderReadonly("accessCode", "Generated when saved");
  const clinicField = field("clinicId");
  const nicknameField = field("nickname");
  const summaryField = field("clinicalSummary");
  const selfInitiatedField = field("selfInitiatedPermitted");
  const badgeLevel = renderReadonly("badgeLevel", "Calculated when saved");
  const selectedClinic = relationOptions.find(
    (option) => option.value === values.clinicId,
  );
  const selectableClinics = relationOptions.filter(
    (option) =>
      option.enabled || (mode === "edit" && option.value === values.clinicId),
  );

  const clinicControl = clinicField ? (
    <FormField field={clinicField}>
      {label(clinicField, `${mode}-client-clinicId`)}
      <select
        id={`${mode}-client-clinicId`}
        className="cp-form-input"
        value={values.clinicId}
        required={required(clinicField)}
        disabled={!editable(clinicField) || isSubmitting}
        aria-invalid={Boolean(validationErrors.clinicId)}
        onChange={(event) => change("clinicId", event.target.value)}
      >
        <option value="" disabled>
          Select a Clinic
        </option>
        {selectableClinics.map((option) => (
          <option
            key={option.id}
            value={option.value}
            disabled={!option.enabled}
          >
            {option.name}
            {option.enabled ? "" : " (Unavailable)"}
          </option>
        ))}
        {mode === "edit" && values.clinicId && !selectedClinic ? (
          <option value={values.clinicId} disabled>
            Current Clinic (Unavailable)
          </option>
        ) : null}
      </select>
      {error("clinicId")}
    </FormField>
  ) : null;

  const nicknameControl = nicknameField ? (
    <FormField field={nicknameField}>
      {label(nicknameField, `${mode}-client-nickname`)}
      <input
        id={`${mode}-client-nickname`}
        className="cp-form-input"
        type="text"
        value={values.nickname}
        required
        readOnly={!editable(nicknameField)}
        disabled={isSubmitting}
        aria-invalid={Boolean(validationErrors.nickname)}
        onChange={(event) => change("nickname", event.target.value)}
      />
      {error("nickname")}
    </FormField>
  ) : null;

  const summaryControl = summaryField ? (
    <FormField field={summaryField}>
      {label(summaryField, `${mode}-client-clinicalSummary`)}
      <textarea
        id={`${mode}-client-clinicalSummary`}
        className="cp-form-input cp-form-textarea"
        rows={5}
        value={values.clinicalSummary}
        required={summaryField.required}
        readOnly={!editable(summaryField)}
        disabled={isSubmitting}
        aria-invalid={Boolean(validationErrors.clinicalSummary)}
        onChange={(event) => change("clinicalSummary", event.target.value)}
      />
      {error("clinicalSummary")}
    </FormField>
  ) : null;

  const selfInitiatedControl = selfInitiatedField ? (
    <FormField field={selfInitiatedField}>
      <label className="cp-form-checkbox cp-client-edit-checkbox">
        <input
          type="checkbox"
          checked={values.selfInitiatedPermitted}
          disabled={!editable(selfInitiatedField) || isSubmitting}
          onChange={(event) =>
            change("selfInitiatedPermitted", event.target.checked)
          }
        />
        <span className="cp-visually-hidden">{selfInitiatedField.label}</span>
      </label>
    </FormField>
  ) : null;

  const editRow = (
    config: PortalRecordFormField | undefined,
    control: ReactNode,
  ) =>
    config && control ? (
      <RecordFieldRow
        label={config.label}
        required={mode === "create" && required(config)}
      >
        {control}
      </RecordFieldRow>
    ) : null;

  if (mode === "edit") {
    return (
      <form
        className="cp-client-edit-form"
        onSubmit={handleSubmit}
        noValidate
        aria-label="Edit Client"
      >
        <ContentSection title="Client Profile">
          <p className="cp-content-section__description">
            Core client information, clinical context, assistant settings, and
            preferences.
          </p>
          <RecordDetailCard>
            {editRow(field("nickname"), nicknameControl)}
            {editRow(field("accessCode"), accessCode)}
            {editRow(field("clinicId"), clinicControl)}
            {editRow(field("status"), renderSingleLov("status"))}
            {editRow(
              field("highSudsThreshold"),
              renderInteger("highSudsThreshold"),
            )}
            {editRow(
              field("sudsInputStyle"),
              renderSingleLov("sudsInputStyle"),
            )}
            {editRow(field("selfInitiatedPermitted"), selfInitiatedControl)}
            {editRow(field("clinicalSummary"), summaryControl)}
            {editRow(
              field("copingStrategies"),
              renderMultiLov("copingStrategies"),
            )}
            {editRow(field("funActivities"), renderMultiLov("funActivities"))}
          </RecordDetailCard>
        </ContentSection>

        <ContentSection title="Progress & Rewards">
          <p className="cp-content-section__description">
            Review the client’s points, coins, current rank, and earned badges.
          </p>
          <RecordDetailCard>
            {editRow(field("pointsEarned"), renderInteger("pointsEarned"))}
            {editRow(field("coins"), renderInteger("coins"))}
            {editRow(field("badgeLevel"), badgeLevel)}
            {editRow(
              field("badgeCollection"),
              renderMultiLov("badgeCollection"),
            )}
          </RecordDetailCard>
        </ContentSection>

        {validationErrors.form ? (
          <p className="cp-form-error cp-form-error--panel" role="alert">
            {validationErrors.form}
          </p>
        ) : null}
        {apiError ? (
          <p className="cp-form-error cp-form-error--panel" role="alert">
            {apiError}
          </p>
        ) : null}

        <div className="cp-form-actions cp-form-actions--sticky">
          <PortalLink
            className="cp-button cp-button--secondary"
            path={cancelPath}
            clinicianCode={clinicianCode}
            aria-disabled={isSubmitting}
            onClick={(event) => {
              if (isSubmitting) event.preventDefault();
            }}
          >
            Cancel
          </PortalLink>
          <button
            type="submit"
            className="cp-button cp-button--primary"
            disabled={isSubmitting}
          >
            {isSubmitting ? "Saving…" : "Save Client"}
          </button>
        </div>
      </form>
    );
  }

  return (
    <form
      className="cp-client-edit-form"
      aria-label="Add Client form"
      onSubmit={handleSubmit}
      noValidate
    >
      <ContentSection title="Client Details">
        <p className="cp-content-section__description">
          Create a client record and review the recommended defaults before
          saving.
        </p>
        <details className="cp-create-form__defaults">
          <summary>
            <span>Recommended defaults applied</span>
            <span className="cp-create-form__defaults-toggle cp-create-form__defaults-toggle--view">
              View details
            </span>
            <span className="cp-create-form__defaults-toggle cp-create-form__defaults-toggle--hide">
              Hide details
            </span>
          </summary>
          <p>{defaultsCallout}</p>
        </details>
        <RecordDetailCard>
          {editRow(field("accessCode"), accessCode)}
          {editRow(field("nickname"), nicknameControl)}
          {editRow(field("clinicId"), clinicControl)}
          {editRow(field("status"), renderSingleLov("status"))}
          {editRow(field("clinicalSummary"), summaryControl)}
          {editRow(
            field("highSudsThreshold"),
            renderInteger("highSudsThreshold"),
          )}
          {editRow(field("sudsInputStyle"), renderSingleLov("sudsInputStyle"))}
          {editRow(field("selfInitiatedPermitted"), selfInitiatedControl)}
          {editRow(
            field("copingStrategies"),
            renderMultiLov("copingStrategies"),
          )}
          {editRow(field("funActivities"), renderMultiLov("funActivities"))}
          {editRow(field("pointsEarned"), renderInteger("pointsEarned"))}
          {editRow(field("coins"), renderInteger("coins"))}
          {editRow(field("badgeLevel"), badgeLevel)}
          {editRow(field("badgeCollection"), renderMultiLov("badgeCollection"))}
        </RecordDetailCard>
      </ContentSection>

      {validationErrors.form ? (
        <p className="cp-form-error cp-form-error--panel" role="alert">
          {validationErrors.form}
        </p>
      ) : null}
      {apiError ? (
        <p className="cp-form-error cp-form-error--panel" role="alert">
          {apiError}
        </p>
      ) : null}

      <div className="cp-form-actions cp-form-actions--sticky">
        <PortalLink
          className="cp-button cp-button--secondary"
          path={cancelPath}
          clinicianCode={clinicianCode}
          aria-disabled={isSubmitting}
          onClick={(event) => {
            if (isSubmitting) event.preventDefault();
          }}
        >
          Cancel
        </PortalLink>
        <button
          type="submit"
          className="cp-button cp-button--primary"
          disabled={isSubmitting}
        >
          {isSubmitting ? "Saving…" : "Save Client"}
        </button>
      </div>
    </form>
  );
}
